import { useCallback, ChangeEvent, ReactElement, KeyboardEvent } from "react";
import classNames from "classnames/dedupe";

import useWatchState from "../../../../hooks/useWatchState";
import noop from "../../../../helpers/noop";
import { TValidation } from "../InputFormProps.interface";
import { TThemeVariant } from "../../../Utility/ThemeProvider";

const blockInvalidChar = (e) => ["e", "E", "+", "-"].includes(e.key);

interface IUseValidation {
  value?: string;
  dirty?: boolean;
  validation?: Array<RegExp>;
  validity?: TValidation;
  onChange?: (
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => void;
  onBlur?: (
    event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => void;
  onFocus?: (
    event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => void;
  disabled?: boolean;
  errorMessage?: string | ReactElement;
  theme: TThemeVariant;
  minValue?: number;
  type?: string;
  onEnter?: (
    event: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => void;
}

const useValidation = ({
  value = "",
  dirty,
  validation = [],
  validity = "none",
  onChange = noop,
  onBlur = noop,
  onFocus = noop,
  disabled = false,
  errorMessage,
  theme = "dark",
  minValue,
  type,
  onEnter,
}: IUseValidation) => {
  const [inputValue] = useWatchState({
    value,
  });
  const [inputValidity, setInputValidity] = useWatchState({ value: validity });

  const [currentDirty, setCurrentDirty] = useWatchState({
    value: dirty,
  });

  const validationPassed = useCallback(() => {
    validation.forEach((elem) => {
      if (!inputValue || elem.test(inputValue)) {
        setInputValidity("valid");
      } else {
        setInputValidity("error");
      }
    });
  }, [inputValue, validation]);

  const handleValidationBlur = useCallback(() => {
    setCurrentDirty(true);
    if (validation.length) {
      validationPassed();
    }
  }, [validationPassed, validation]);

  const onInputChange = (e) => {
    if (
      type !== "number" ||
      (type === "number" && e.target.value >= minValue) ||
      e.target.value === ""
    ) {
      onChange(e);
    }
  };

  const onKeyDownValidation = useCallback(
    (e: KeyboardEvent) => {
      const isEnterPressed = e.key.toLowerCase() === "enter";

      if ((type === "number" && blockInvalidChar(e)) || isEnterPressed) {
        e.preventDefault();
        validationPassed();
      }

      isEnterPressed && onEnter?.(e as React.KeyboardEvent<any>);
    },
    [inputValue, validation, onEnter, validationPassed],
  );

  /** Set 'dirty' on blur */
  const handleBlur = useCallback(
    (event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      onBlur?.(event);
      handleValidationBlur();
    },
    [onBlur, handleValidationBlur],
  );

  const handleFocus = useCallback(
    (event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) =>
      onFocus?.(event),
    [onFocus],
  );

  const hasErrorMessage =
    !disabled && currentDirty && inputValidity === "error" && errorMessage;

  const validationClasses = classNames({
    "is-valid": inputValidity === "valid",
    "is-invalid": inputValidity === "error",
    "is-pristine": !currentDirty,
    "is-dirty": currentDirty,
  });

  const floatingLineClasses = classNames("form-floating-line", {
    "d-block bg-color-error":
      !disabled && currentDirty && inputValidity === "error",
  });

  const errorMessageClasses = classNames("invalid-feedback", {
    "text-color-white": theme === "dark",
  });

  const descriptionClasses = classNames("form-text", {
    "text-color-white": theme === "dark",
  });

  return {
    inputValidity,
    currentDirty,
    validationPassed,
    handleValidationBlur,
    inputValue,
    onInputChange,
    onKeyDownValidation,
    handleBlur,
    handleFocus,
    hasErrorMessage,
    validationClasses,
    floatingLineClasses,
    errorMessageClasses,
    descriptionClasses,
  };
};

export default useValidation;
